// Sample data generator for demonstration
const generateSampleBookings = () => {
  const statuses = ['pending', 'confirmed', 'checked_in', 'checked_out', 'cancelled']
  const roomTypes = ['Single Room', 'Double Room', 'Deluxe Room', 'Suite', 'Studio Apartment', 'One Bedroom Apartment']
  const guests = [
    { name: 'John Smith', email: 'john.smith@email.com', phone: '+1234567890' },
    { name: 'Sarah Johnson', email: 'sarah.j@email.com', phone: '+1234567891' },
    { name: 'Michael Brown', email: 'm.brown@email.com', phone: '+1234567892' },
    { name: 'Emma Wilson', email: 'emma.w@email.com', phone: '+1234567893' },
    { name: 'David Lee', email: 'd.lee@email.com', phone: '+1234567894' }
  ]

  const bookings = []
  const today = new Date()

  for (let i = 1; i <= 25; i++) {
    const checkIn = new Date(today)
    checkIn.setDate(today.getDate() + Math.floor(Math.random() * 60) - 30)
    const checkOut = new Date(checkIn)
    checkOut.setDate(checkIn.getDate() + Math.floor(Math.random() * 7) + 1)

    const guest = guests[Math.floor(Math.random() * guests.length)]
    const status = statuses[Math.floor(Math.random() * statuses.length)]

    bookings.push({
      id: i,
      booking_code: `BK${String(i).padStart(6, '0')}`,
      room_id: Math.floor(Math.random() * 20) + 1,
      room_number: `${Math.floor(Math.random() * 4) + 1}${String(Math.floor(Math.random() * 20) + 1).padStart(2, '0')}`,
      room_type: roomTypes[Math.floor(Math.random() * roomTypes.length)],
      guest_name: guest.name,
      guest_email: guest.email,
      guest_phone: guest.phone,
      check_in_date: checkIn.toISOString().split('T')[0],
      check_out_date: checkOut.toISOString().split('T')[0],
      number_of_guests: Math.floor(Math.random() * 4) + 1,
      total_amount: Math.floor(Math.random() * 500) + 100,
      paid_amount: status === 'checked_out' ? Math.floor(Math.random() * 500) + 100 : 0,
      status: status,
      special_requests: Math.random() > 0.5 ? 'Late check-in requested' : '',
      created_at: new Date(today.getTime() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
    })
  }

  return bookings
}

export default class CategoryService {
  constructor (http, store, router) {
    this.$http = http
    this.$store = store
    this.$router = router
  }

  // Get all bookings with filters
  getBookings (params = {}) {
    // For demo purposes, return sample data
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          data: generateSampleBookings(),
          total: 25,
          current_page: 1,
          per_page: 20
        }
      })
    }

    return this.$http.get('/bookings', { params })
  }

  // Get single booking details
  getBooking (id) {
    if (process.env.VUE_APP_DEMO_MODE) {
      const bookings = generateSampleBookings()
      const booking = bookings.find(b => b.id === parseInt(id))
      return Promise.resolve({ data: booking })
    }

    return this.$http.get(`/bookings/${id}`)
  }

  // Create new booking
  createBooking (data) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          ...data,
          id: Date.now(),
          booking_code: `BK${String(Date.now()).slice(-6)}`,
          status: 'pending',
          created_at: new Date().toISOString()
        }
      })
    }

    return this.$http.post('/bookings', data)
  }

  // Update booking
  updateBooking (id, data) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({ data: { ...data, id } })
    }

    return this.$http.put(`/bookings/${id}`, data)
  }

  // Cancel booking
  cancelBooking (id, reason) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          id,
          status: 'cancelled',
          cancellation_reason: reason,
          cancelled_at: new Date().toISOString()
        }
      })
    }

    return this.$http.post(`/bookings/${id}/cancel`, { reason })
  }

  // Check-in guest
  checkIn (id, data = {}) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          id,
          status: 'checked_in',
          actual_check_in_time: new Date().toISOString(),
          ...data
        }
      })
    }

    return this.$http.post(`/bookings/${id}/check-in`, data)
  }

  // Check-out guest
  checkOut (id, data = {}) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          id,
          status: 'checked_out',
          actual_check_out_time: new Date().toISOString(),
          final_amount: data.final_amount || 0,
          ...data
        }
      })
    }

    return this.$http.post(`/bookings/${id}/check-out`, data)
  }

  // Get booking availability
  checkAvailability (params) {
    if (process.env.VUE_APP_DEMO_MODE) {
      // Generate sample available rooms
      const rooms = []
      for (let i = 1; i <= 10; i++) {
        rooms.push({
          id: i,
          room_number: `${Math.floor(i / 5) + 1}${String(i % 5 + 1).padStart(2, '0')}`,
          room_type: ['Single Room', 'Double Room', 'Deluxe Room', 'Suite'][Math.floor(Math.random() * 4)],
          price_per_night: Math.floor(Math.random() * 200) + 50,
          available: Math.random() > 0.3
        })
      }
      return Promise.resolve({ data: rooms.filter(r => r.available) })
    }

    return this.$http.get('/bookings/availability', { params })
  }

  // Get booking statistics
  getBookingStats (params = {}) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          total_bookings: 156,
          confirmed_bookings: 89,
          current_occupancy: 67,
          revenue_this_month: 45680,
          average_stay_duration: 3.2,
          cancellation_rate: 8.5
        }
      })
    }

    return this.$http.get('/bookings/stats', { params })
  }

  // Generate invoice for booking
  generateInvoice (id) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          invoice_number: `INV-${String(Date.now()).slice(-6)}`,
          booking_id: id,
          issued_date: new Date().toISOString(),
          items: [
            { description: 'Room Charges', amount: 350 },
            { description: 'Service Charge', amount: 35 },
            { description: 'Tax (10%)', amount: 38.5 }
          ],
          total_amount: 423.5
        }
      })
    }

    return this.$http.get(`/bookings/${id}/invoice`)
  }

  // Add payment to booking
  addPayment (bookingId, paymentData) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          id: Date.now(),
          booking_id: bookingId,
          amount: paymentData.amount,
          payment_method: paymentData.payment_method,
          payment_date: new Date().toISOString(),
          reference_number: `PAY-${String(Date.now()).slice(-6)}`
        }
      })
    }

    return this.$http.post(`/bookings/${bookingId}/payments`, paymentData)
  }

  // Get payment history for booking
  getPaymentHistory (bookingId) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: [
          {
            id: 1,
            amount: 100,
            payment_method: 'Credit Card',
            payment_date: new Date().toISOString(),
            reference_number: 'PAY-123456',
            status: 'completed'
          }
        ]
      })
    }

    return this.$http.get(`/bookings/${bookingId}/payments`)
  }
}
